import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePriceIndexStore } from '../../store/usePriceIndexStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Badge } from '../../components/Shared/Badge.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { EmptyState } from '../../components/Shared/EmptyState.tsx';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table.tsx';
import { TrendingUp, Plus, CheckCircle2 } from 'lucide-react';
import { formatPercent } from '../../utils/percent';
import PriceIndexDialog from '../../components/Shared/PriceIndexDialog.tsx';

export default function PriceIndexHistory() {
  const { t } = useTranslation(['priceIndex', 'common']);
  const navigate = useNavigate();
  const { priceIndexes, loading, error, fetchPriceIndexes, createPriceIndex } = usePriceIndexStore();
  const [isPriceIndexDialogOpen, setIsPriceIndexDialogOpen] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    fetchPriceIndexes().then(() => setInitialLoadComplete(true));
  }, [fetchPriceIndexes]);

  const handleCreatePriceIndex = async (data: {
    designation: string;
    factor: number;
    effectiveFrom: string;
    description?: string;
  }) => {
    await createPriceIndex(data);
    await fetchPriceIndexes();
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getStatusBadge = (status?: string | null) => {
    switch (status) {
      case 'Active':
        return <Badge variant="success">{t('priceIndex:status.active')}</Badge>;
      case 'Draft':
        return <Badge variant="warning">{t('priceIndex:status.draft')}</Badge>;
      case 'Inactive':
        return <Badge variant="default">{t('priceIndex:status.inactive')}</Badge>;
      default:
        return <Badge variant="default">{status || '-'}</Badge>;
    }
  };

  const getFactorDisplay = (factor?: number) => {
    if (!factor) return '-';
    return `${factor.toFixed(2)} (${formatPercent((factor - 1) * 100, { signed: true, fractionDigits: 1 })})`;
  };

  if (loading && !initialLoadComplete) return <Loading />;

  if (error && !initialLoadComplete) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary dark:text-slate-50">
              {t('priceIndex:title')}
            </h1>
            <p className="text-text-secondary dark:text-slate-400 mt-1">
              {t('priceIndex:subtitle')}
            </p>
          </div>
        </div>
        <Card>
          <EmptyState
            variant="error"
            title={t('priceIndex:errors.loadError')}
            description={error || t('priceIndex:errors.loadErrorDescription')}
            onRetry={() => fetchPriceIndexes()}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary dark:text-slate-50">
            {t('priceIndex:title')}
          </h1>
          <p className="text-sm sm:text-base text-text-secondary dark:text-slate-400 mt-1">
            {t('priceIndex:subtitleDetailed')}
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setIsPriceIndexDialogOpen(true)}
          className="w-full sm:w-auto justify-center"
        >
          <span className="hidden sm:inline">{t('priceIndex:buttons.newPriceIndex')}</span>
          <span className="sm:hidden">{t('priceIndex:buttons.create')}</span>
        </Button>
      </div>

      {/* Price Index Table */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('priceIndex:errors.loadError')}
            description={error || t('priceIndex:errors.loadErrorDescription')}
            onRetry={() => fetchPriceIndexes()}
          />
        </Card>
      ) : priceIndexes.length === 0 ? (
        <EmptyState
          icon={<TrendingUp className="w-12 h-12" />}
          title={t('priceIndex:empty.title')}
          description={t('priceIndex:empty.description')}
          action={
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setIsPriceIndexDialogOpen(true)}
            >
              {t('priceIndex:buttons.createPriceIndex')}
            </Button>
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader className="hidden sm:table-cell">{t('priceIndex:table.status')}</TableHeader>
                <TableHeader>{t('priceIndex:table.designation')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('priceIndex:table.factor')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('priceIndex:table.validFrom')}</TableHeader>
                <TableHeader className="hidden xl:table-cell">{t('priceIndex:table.appliedAt')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('priceIndex:table.affectedServices')}</TableHeader>
                <TableHeader className="hidden xl:table-cell">{t('priceIndex:table.description')}</TableHeader>
                <TableHeader>{t('priceIndex:table.actions')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {priceIndexes.map((priceIndex) => (
                <TableRow 
                  key={priceIndex.id}
                  className={
                    priceIndex.status === 'Active'
                      ? 'bg-green-50 dark:bg-green-900/10 border-l-4 border-green-500'
                      : ''
                  }
                >
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex items-center gap-2">
                      {priceIndex.status === 'Active' && (
                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                      )}
                      {getStatusBadge(priceIndex.status)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className={
                        priceIndex.status === 'Active'
                          ? 'font-semibold text-sm sm:text-base text-gray-900 dark:text-white'
                          : 'font-medium text-sm sm:text-base text-gray-900 dark:text-white'
                      }>
                        {priceIndex.designation || '-'}
                      </div>
                      {/* Mobile: Show badge and factor */}
                      <div className="sm:hidden text-xs text-text-secondary dark:text-slate-400 mt-0.5 space-y-1">
                        <div className="flex items-center gap-2">
                          {priceIndex.status === 'Active' && (
                            <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400" />
                          )}
                          {getStatusBadge(priceIndex.status)}
                        </div>
                        <div className={
                          priceIndex.status === 'Active'
                            ? 'font-medium text-green-700 dark:text-green-400'
                            : ''
                        }>
                          {getFactorDisplay(priceIndex.factor)}
                        </div>
                        <div>{t('priceIndex:table.from')} {formatDate(priceIndex.effectiveFrom)}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className={
                      priceIndex.status === 'Active'
                        ? 'font-medium text-sm sm:text-base text-green-700 dark:text-green-400'
                        : 'text-sm sm:text-base'
                    }>
                      {getFactorDisplay(priceIndex.factor)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-xs sm:text-sm">
                      {formatDate(priceIndex.effectiveFrom)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <span className="text-xs sm:text-sm">
                      {formatDateTime(priceIndex.appliedAt)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-xs sm:text-sm">
                      {priceIndex.affectedServicesCount || 0} {t('common:general.services')}
                    </span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                      {priceIndex.description || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/price-indexes/${priceIndex.id}/preview`)}
                      className="text-xs sm:text-sm px-2 sm:px-3 py-1"
                    >
                      <span className="hidden sm:inline">{t('priceIndex:buttons.preview')}</span>
                      <span className="sm:hidden">→</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Price Index Dialog */}
      <PriceIndexDialog
        isOpen={isPriceIndexDialogOpen}
        onClose={() => setIsPriceIndexDialogOpen(false)}
        onSubmit={handleCreatePriceIndex}
      />
    </div>
  );
}
